import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const EASE = [0.22, 1, 0.36, 1];

// Small lime diamond used as a phrase separator
function Diamond() {
  return (
    <span
      aria-hidden
      className="inline-block w-2.5 h-2.5 md:w-3 md:h-3 bg-[color:var(--collab-lime)] rotate-45 origin-center"
    />
  );
}

// 5 work categories — one row on desktop, uniform square tiles.
// Square is the gentlest common crop for these sources (booth is natively
// square; the rest are landscape with centred subjects) — no subject is cut.
const WORK_CATEGORIES = [
  {
    label: "Brand Activation & Events",
    slug: "on-ground-activations",
    image: "/collage/brand-activation.webp",
  },
  {
    label: "Conferences, Launches & Networking Events",
    slug: "conferences-launches-networking",
    image: "/collage/conferences.webp",
  },
  {
    label: "Government Events & Summits",
    slug: "government-events-summits",
    image: "/collage/government.webp",
  },
  {
    label: "Sports Events & Activations",
    slug: "sports-events-activations",
    image: "/collage/sports.webp",
  },
  {
    label: "Retail & Exhibition",
    slug: "retail-exhibition",
    image: "/collage/teachers.jpg",
  },
];

export default function Team() {
  return (
    <section
      id="team"
      data-testid="team-section"
      className="shell py-12 md:py-16 bg-[color:var(--collab-cream-2)]"
    >
      {/* Heading + brand statement */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="border-b border-[color:var(--collab-hairline)] pb-8 md:pb-10 text-center"
      >
        <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] text-[color:var(--collab-ink)]">
          OUR WORK
          <br />
          <em
            className="inline-flex items-center justify-center flex-wrap gap-x-3 md:gap-x-5 gap-y-1 italic font-medium text-[color:var(--collab-forest)] text-xl md:text-3xl lg:text-4xl leading-[1.2] mt-4"
            data-testid="work-statement"
          >
            <span className="italic">Big Ideas</span>
            <Diamond />
            <span className="italic">Smart Design</span>
            <Diamond />
            <span className="italic">Seamless Delivery</span>
          </em>
        </h2>
      </motion.div>

      {/* Five categories — single row on desktop, identical square tiles */}
      <div className="mt-14 md:mt-20 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 md:gap-6">
        {WORK_CATEGORIES.map((w, i) => (
          <motion.figure
            key={w.slug}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, ease: EASE, delay: i * 0.07 }}
            className="group flex flex-col"
            data-testid={`work-category-${w.slug}`}
          >
            <Link to={`/work/${w.slug}`} aria-label={`Explore ${w.label}`}>
              <div className="relative w-full aspect-square overflow-hidden bg-[color:var(--collab-forest-deep)]">
                <img
                  src={w.image}
                  alt={w.label}
                  className={`absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[900ms] ease-out ${
                    w.zoom || "scale-[1.02] group-hover:scale-105"
                  }`}
                  loading="lazy"
                />
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-500" />
              </div>
              <figcaption className="mt-3 font-display text-base md:text-lg leading-snug text-[color:var(--collab-ink)] balance text-center">
                {w.label}
              </figcaption>
            </Link>
          </motion.figure>
        ))}
      </div>

    </section>
  );
}
